import { z } from 'zod';
import { studyGroupZodEnum } from '../../enum/study-group.zod-enum';
import { bdm1OutcomeZodEnum } from '../../enum/bdm1-outcome.zod-enum';
import { BDM2_OUTCOME_VARIANTS } from '../../calculations/bdm2-outcome-variant';
import {
  bdm1VoucherStatusSchema,
  bdm2VoucherStatusSchema,
} from '../voucher/survey-link-voucher-status.res.dto';

export const bdmSurveyResDto = z.object({
  link: z.string(),
  filledOutAt: z.coerce.date().nullable(),
});
export type BdmSurveyResDto = z.infer<typeof bdmSurveyResDto>;

// The pair of BDM surveys the backend resolves for a GROUP_1 participant. Each
// is null until its link was imported (ETH randomization list); which one to
// display, and when, is the frontend's decision.
export const bdmSurveysResDto = z.object({
  bdm1Survey: bdmSurveyResDto.nullable(),
  bdm2Survey: bdmSurveyResDto.nullable(),
});
export type BdmSurveysResDto = z.infer<typeof bdmSurveysResDto>;

// The imported BDM2 draw: which product was drawn in which survey round, at which
// price, against the participant's own stated max price, and how the rest of the 80 CHF
// is paid out. The variant says which of the seven approved texts describes this result;
// it follows from the amounts and the frozen Retrieval.retrievalMode, never from the
// treatment arm.
export const bdm2OutcomeResDto = z.object({
  variant: z.enum(BDM2_OUTCOME_VARIANTS),
  drawnProduct: z.string(),
  drawnSurveyRound: z.number(),
  drawnPrice: z.number(),
  maxPriceOfDrawnProduct: z.number(),
  galaxusValue: z.number(),
  cashMoney: z.number(),
});
export type Bdm2OutcomeResDto = z.infer<typeof bdm2OutcomeResDto>;

// Note: bdmTreatment (Control/Info/Exp/InfoExp) is deliberately NOT part of
// this response. The participant must stay blind to their treatment arm, and
// the frontend has no use for it — the backend resolves treatment -> the
// correct survey link server-side. Do not add it here.
export const participantBdmStateResDto = z
  .object({
    studyGroup: studyGroupZodEnum().nullable(),
    // BDM day 0 of the participant's cohort; anchor for the BDM deadlines and the
    // part-2 randomization day (constants/bdm-timeline). Sent for every study group —
    // GROUP_2 waits on the same part-2 day. null = cohort not configured for BDM.
    bdmStartDate: z.coerce.date().nullable(),
    bdm1Outcome: bdm1OutcomeZodEnum().nullable(),
    bdm1VoucherStatus: bdm1VoucherStatusSchema,
    // Galaxus portion of the BDM2 outcome only; the cash remainder is staff-settled and
    // therefore not part of a voucher status (the amount itself is shown, see bdm2Outcome).
    bdm2VoucherStatus: bdm2VoucherStatusSchema,
    // The drawn BDM2 result, null until the ETH outcome file was imported and its
    // Retrieval row created — and always null outside GROUP_1.
    bdm2Outcome: bdm2OutcomeResDto.nullable(),
    // May a Nachversand still be booked at all? Participant-wide, not per kind, and the
    // same value the booking webhook gates on, so the UI and the server cannot drift
    // apart. null in the DB ("not computed yet") arrives here as false.
    nachversandEligible: z.boolean(),
  })
  .extend(bdmSurveysResDto.shape);
export type ParticipantBdmStateResDto = z.infer<
  typeof participantBdmStateResDto
>;
